/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { faMobile, faDesktop } from '@fortawesome/pro-regular-svg-icons';

import { useModal } from '@client/components/common/Modal';

import {
  useAddPurchasePageConfigMutation,
  useUpdatePurchasePageConfigMutation,
  usePurchasePageConfigQuery,
  useLandingPageConfigQuery,
  useGetInfluencerQuery,
  useGetOfferQuery,
  useOfferLandingPageConfigQuery,
  useAddOfferLandingPageConfigMutation,
  useUpdateOfferLandingPageConfigMutation,
  OfferLandingPageConfigDocument,
  PurchasePageConfigDocument,
  BundleStatus,
} from '../../../generated/graphql';
import { PreviewModal, PreviewType } from './Modals';
import { Container as SectionContainer } from '@client/new/pages/LandingPagesBuilder/container/Container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ISection, PurchasePageConfigSchemaV1 } from './schemas';
import {
  useGetProgramPurchasePageByIdQuery,
  GetProgramPurchasePageByIdQuery,
} from '../../../generated/graphql';
import { removeAlpha } from './helpers';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import {
  defaultOTPPageConfig,
  loadOfferPageConfig,
  loadProgramPageConfig,
} from './defaultConfig';
import { muiForm } from '@client/new/helpers/forms';
import { toast } from 'react-hot-toast';
import { useSections } from '@client/new/pages/LandingPagesBuilder/hooks';
import { F1 } from '@client/new/helpers/types';
import { isEmpty, keyBy } from 'lodash';
import { toProgramLandingPageUrl } from '@client/helpers/routes';
import { PATHS, navigate } from '@client/routes';
import { UnsavedChangesRedirectModal } from './UnsavedChangesRedirectModal';
import { useInfluencerAssetsUploader } from '@client/new/pages/LandingPagesBuilder/helpers/upload';
import { useRouterWillLeave } from '@client/new/hooks/useRouterWillLeave';
import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { CustomDomainBanner } from '@client/new/components/CustomDomainBanner';
import { sprinkles } from '@design-system/sprinkles.css';
import { pageBackground } from '@client/new/pages/page-background.css';
import { Button as NewButton } from '@design-system/components/button/button';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { CircularProgress } from '@mui/material';
import { Influencer } from '@playbook-technologies/common-enums';
import { defaultErrorMap, ZodIssue, ZodIssueCode } from 'zod';
import { SectionRenderer } from './SectionRenderer';
import { Errors } from '@client/new/pages/promotions/view/components/Errors';
import {
  cleanupSectionsType,
  ensureIntroductionDefaults,
  stripOfferUiFields,
  deriveWorkoutsPreviewEnabled,
  extendSectionsType,
  onEnabledChange,
  toErrors,
  toSectionTitle,
} from './mapper';
import {
  OTPLandingPageSchema,
  SectionSchema,
  OfferDetailsSectionSchema,
} from './OTPLandingPage.schema';
import { toOfferDetailsData, OfferDetailsData } from './OfferDetails.mappers';
import { getOfferLandingPageUrl, LandingDomainOwner } from './landingPageUrls';
import { trackEvent } from '@services/tracking';
import { HELP_LINKS } from '@client/new/common/helpLinks';
import { useCustomizations } from '@client/new/hooks/use-customizations';

type CreatorData = GetProgramPurchasePageByIdQuery['program']['influencer'];
const CreatorContext = React.createContext<CreatorData>(null);
const ProgramContext =
  React.createContext<GetProgramPurchasePageByIdQuery['program']>(null);

export function useProgram() {
  const context = React.useContext(ProgramContext);

  // Allow null in offer mode - return null instead of throwing
  return context;
}

export function useCreator() {
  const context = React.useContext(CreatorContext);

  // Allow null in offer mode - return null instead of throwing
  return context;
}

function useDataLoader(
  programId: number,
  influencerId: number,
  offerId?: number,
) {
  const {
    data: programData,
    loading: isLoadingProgram,
    error: programError,
  } = useGetProgramPurchasePageByIdQuery({
    variables: {
      id: programId,
    },
    skip: !!offerId, // Skip program query in offer mode
  });

  // Conditional fetching for OTP mode (no offerId)
  const {
    data: otpConfigData,
    loading: isLoadingOTPConfig,
    error: otpConfigError,
  } = usePurchasePageConfigQuery({
    variables: {
      programId,
    },
    skip: !!offerId, // Skip if offerId exists
    fetchPolicy: 'network-only', // Always fetch fresh data from server
  });

  // Conditional fetching for Offer mode (with offerId)
  const {
    data: offerConfigData,
    loading: isLoadingOfferConfig,
    error: offerConfigError,
  } = useOfferLandingPageConfigQuery({
    variables: {
      influencerId,
      parentId: offerId || 0,
    },
    skip: !offerId, // Skip if no offerId
    fetchPolicy: 'network-only', // Always fetch fresh data from server
  });

  const {
    data: landingConfigData,
    loading: isLoadingLandingConfig,
    error: creatorLandingConfigError,
  } = useLandingPageConfigQuery({
    variables: {
      influencerId,
      creatorId: influencerId + '',
    },
  });

  const influencerQuery = useGetInfluencerQuery({
    variables: { id: influencerId },
  });

  // Conditionally fetch offer data if offerId is provided
  const offerQuery = useGetOfferQuery({
    variables: {
      creatorIdID: influencerId + '',
      creatorIdInt: influencerId,
      bundleId: offerId || 0,
    },
    skip: !offerId,
  });

  const offerData: OfferDetailsData | undefined = offerQuery.data
    ? toOfferDetailsData(offerQuery.data)
    : undefined;
  const offerStatus = offerQuery.data?.bundleById?.status ?? null;

  const normalizeOfferTitle = (raw: unknown): string | null => {
    if (!raw) return null;

    const bundleId = offerQuery.data?.bundleById?.id;

    if (typeof raw === 'string') {
      if (!raw.includes('[object Object]')) return raw;

      const replacement = bundleId != null ? `#${bundleId}` : '';
      const cleaned = raw
        .replace('#[object Object]', replacement)
        .replace('[object Object]', replacement)
        .replace(/\s+#$/, '')
        .trim();

      if (cleaned.length > 0) return cleaned;
      return bundleId != null ? `Offer #${bundleId}` : null;
    }

    return bundleId != null ? `Offer #${bundleId}` : null;
  };

  const offerTitle = normalizeOfferTitle(offerQuery.data?.bundleById?.title);
  const offerDescription = (() => {
    const description = offerQuery.data?.bundleById?.description;
    if (typeof description !== 'string') return null;
    const trimmed = description.trim();
    return trimmed.length > 0 ? trimmed : null;
  })();

  const isCreatorLive =
    influencerQuery?.data?.creator.mode === Influencer.Mode.Live.value;

  const creatorLandingBackendConfig = landingConfigData?.landingPageConfig ?? {
    config: null,
    id: null,
  };

  // Merge configs based on mode - normalize response structure
  const backendConfig = offerId
    ? offerConfigData?.landingPageConfig ?? { config: null, id: null }
    : otpConfigData?.purchasePageConfig ?? { config: null, id: null };

  const loading =
    (!offerId && isLoadingProgram) ||
    isLoadingOTPConfig ||
    isLoadingOfferConfig ||
    isLoadingLandingConfig ||
    influencerQuery.loading;
  const error =
    (!offerId && programError) ||
    otpConfigError ||
    offerConfigError ||
    creatorLandingConfigError ||
    influencerQuery.error;

  const res = {
    program: offerId ? undefined : programData?.program,
    backendConfig,
    creatorLandingBackendConfig,
    error,
    loading,
    isCreatorLive,
    offerData,
    offerTitle,
    offerDescription,
    isOfferMode: !!offerId, // NEW: flag indicating offer mode
    creator: influencerQuery.data?.creator ?? null,
    offerStatus,
  };

  if (res?.backendConfig?.config?.sections) {
    return {
      ...res,
      backendConfig: {
        ...res.backendConfig,
        config: {
          ...res.backendConfig.config,
          sections: extendSectionsType(res.backendConfig.config.sections),
        },
      },
    };
  }

  return res;
}

function DataLoader(
  props: { children: F1<any, JSX.Element> } & Pick<
    CreatorPurchasePageConfigProps,
    'params' | 'onOfferNameChange' | 'onDirtyChange'
  >,
) {
  const programId = Number(props.params.programId);
  const influencerId = Number(props.params.influencerId);
  // Ensure offerId is truly undefined if not present (not empty string, not NaN)
  const offerId =
    props.params.offerId && props.params.offerId !== ''
      ? Number(props.params.offerId)
      : undefined;

  const data = useDataLoader(programId, influencerId, offerId);
  if (data.loading) return <CircularProgress size={48} />;
  if (data.error) {
    return <div>Error {data.error.message} (DE)</div>;
  }
  return props.children({ ...props, ...data });
}

type CreatorPurchasePageConfigProps = {
  params: { influencerId: string; programId?: string; offerId?: string };
  embedded?: boolean;
  readonly?: boolean;
  onOfferNameChange?: (name: string | null) => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

export function OTPLandingPageBuilder(props: CreatorPurchasePageConfigProps) {
  const content = (
    <div
      className={sprinkles({
        py: 'm',
        px: 'xl',
        background: 'W-100/50',
        borderRadius: 'l',
      })}
      style={{ maxWidth: '1200px', minHeight: '80vh' }}
    >
      <CustomDomainBanner influencerId={Number(props.params.influencerId)} />
      <Col
        style={{
          alignItems: 'center',
        }}
      >
        <DataLoader {...props}>
          {(innerProps) => (
            <OTPBuilder {...innerProps} onDirtyChange={props.onDirtyChange} />
          )}
        </DataLoader>
      </Col>
    </div>
  );

  // When embedded inside another builder (offer/program), the surrounding
  // layout owns the background. As a standalone page it paints the dashboard
  // background locally (previously applied by the `withLegacyBackground` HOC).
  if (props.embedded) {
    return content;
  }

  return <div className={pageBackground}>{content}</div>;
}

type Props = CreatorPurchasePageConfigProps &
  ReturnType<typeof useDataLoader> & {
    isCreatorLive: boolean;
    onDirtyChange?: (isDirty: boolean) => void;
  };

const BLACK = '000000';

function OTPBuilder(props: Props) {
  const influencerId = Number(props.params.influencerId);
  const programId = Number(props.params.programId);
  const { universalUpload, isUploading } =
    useInfluencerAssetsUploader(influencerId);
  const { customizations } = useCustomizations(props.params.influencerId);
  const isOffers35Enabled = customizations?.isOffers35Enabled === true;

  const program = props.program;
  const offerData = props.offerData;
  const offerTitle = props.offerTitle;
  const offerDescription = props.offerDescription;
  const offerStatus = props.offerStatus;
  const { onOfferNameChange, isOfferMode } = props;
  const landingDomainOwner: LandingDomainOwner | null =
    program?.influencer != null
      ? {
          customURL: program.influencer.customURL,
          customDomains: program.influencer.customDomains,
        }
      : props.creator
      ? {
          customURL: props.creator.customURL ?? null,
          customDomains: props.creator.customDomains ?? null,
        }
      : null;
  const offerLandingPageUrl = props.isOfferMode
    ? getOfferLandingPageUrl(
        landingDomainOwner,
        props.params.offerId ?? undefined,
      )
    : null;
  const isOfferPublished = offerStatus === BundleStatus.Published;

  useEffect(() => {
    if (!onOfferNameChange) return;
    if (!isOfferMode) {
      onOfferNameChange(null);
      return;
    }
    onOfferNameChange(offerTitle);
  }, [isOfferMode, offerTitle, onOfferNameChange]);

  const serverConfig = props.backendConfig?.config as
    | (Record<string, unknown> & { sections?: ISection[] | null })
    | null;
  const landingPageConfig = props.creatorLandingBackendConfig?.config;

  const serverConfigSections = Array.isArray(serverConfig?.sections)
    ? (serverConfig?.sections as ISection[])
    : null;

  const [persistedConfig, setPersistedConfig] = useState<
    (Record<string, unknown> & { sections?: ISection[] | null }) | null
  >(() =>
    serverConfigSections
      ? {
          ...serverConfig,
          sections: extendSectionsType(serverConfigSections),
        }
      : null,
  );

  const serverConfigSignature = useMemo(
    () => (serverConfigSections ? JSON.stringify(serverConfigSections) : null),
    [serverConfigSections],
  );
  const persistedConfigSignature = useMemo(
    () =>
      Array.isArray(persistedConfig?.sections)
        ? JSON.stringify(persistedConfig?.sections)
        : null,
    [persistedConfig],
  );

  useEffect(() => {
    if (!serverConfigSections) return;
    if (serverConfigSignature === persistedConfigSignature) return;
    setPersistedConfig({
      ...serverConfig,
      sections: extendSectionsType(serverConfigSections),
    });
  }, [
    serverConfig,
    serverConfigSections,
    serverConfigSignature,
    persistedConfigSignature,
  ]);

  const configScopeKey = `${programId}:${props.params.offerId ?? 'otp'}`;
  const persistedScopeRef = useRef(configScopeKey);
  useEffect(() => {
    if (persistedScopeRef.current === configScopeKey) return;
    persistedScopeRef.current = configScopeKey;
    setPersistedConfig(
      serverConfigSections
        ? {
            ...serverConfig,
            sections: extendSectionsType(serverConfigSections),
          }
        : null,
    );
  }, [configScopeKey, serverConfig, serverConfigSections]);

  const configDefaults = useMemo(() => {
    if (!persistedConfig?.sections || !Array.isArray(persistedConfig.sections))
      return null;
    return {
      ...persistedConfig,
      sections: deriveWorkoutsPreviewEnabled(persistedConfig.sections),
    } as { sections: ISection[] };
  }, [persistedConfig]);

  const section0 = configDefaults?.sections?.[0];
  const section0PrimaryColor =
    section0?.type === 'Introduction' ? section0.primaryColor : undefined;

  const [primaryColor, setPrimaryColor] = useState(
    section0PrimaryColor || '#' + (program?.color || BLACK).toUpperCase(),
  );

  // Note: We no longer use local state for isEditing/configId.
  // Instead, we use props.backendConfig.id directly to determine CREATE vs UPDATE.

  const colors = useMemo(
    () => [primaryColor, removeAlpha(primaryColor, 0.1), '#FFFFFF'],
    [primaryColor],
  );
  const defaultFallbackValues: { sections: ISection[]; error?: unknown } =
    useMemo(() => {
      try {
        return loadProgramPageConfig(
          program,
          null,
          landingPageConfig,
          { colors, primaryColor },
          {
            isOfferMode: props.isOfferMode,
            offerData,
            offerMetadata: {
              title: offerTitle,
              description: offerDescription,
            },
          },
        );
      } catch (e) {
        return { sections: [], error: e };
      }
    }, [
      program,
      colors,
      primaryColor,
      props.isOfferMode,
      landingPageConfig,
      offerData,
      offerTitle,
      offerDescription,
    ]);

  const hasConfigError = !!defaultFallbackValues.error && !configDefaults;

  const defaultValues: { sections: ISection[] } =
    configDefaults ?? defaultFallbackValues;

  const resolver = zodResolver(OTPLandingPageSchema, {
    errorMap: (error, _ctx) => {
      if (
        error.code === ZodIssueCode.invalid_type &&
        (error.received === 'undefined' || error.received === 'null')
      )
        return { message: 'Cannot be empty' };

      return defaultErrorMap(error, _ctx);
    },
  });

  const formMethods = muiForm(
    useForm<{ sections: ISection[] }>({
      resolver,
      defaultValues,
    }),
  );

  const {
    handleSubmit,
    setValue,
    watch,
    formState,
    reset,
    setError,
    clearErrors,
  } = formMethods;

  const isDirty = !isEmpty(formState.dirtyFields);
  const [isSaving, setIsSaving] = useState(false);

  // Redirect-with-unsaved-changes flow (used by workout thumbnail clicks).
  const [confirmRedirectUrl, setConfirmRedirectUrl] = useState<string | null>(
    null,
  );
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState<string | null>(
    null,
  );
  const afterSaveRedirectRef = useRef<string | null>(null);

  // Navigate only once the form is clean, so the route-leave guard
  // (useRouterWillLeave) doesn't prompt on top of our own modal.
  useEffect(() => {
    if (pendingRedirectUrl && !isDirty) {
      const url = pendingRedirectUrl;
      setPendingRedirectUrl(null);
      navigate(url);
    }
  }, [pendingRedirectUrl, isDirty]);

  useEffect(() => {
    if (!configDefaults) return;
    if (isDirty) return;
    reset(configDefaults);
  }, [configDefaults, isDirty, reset]);

  useEffect(() => {
    if (configDefaults) return;
    if (isDirty) return;
    reset(defaultFallbackValues);
  }, [configDefaults, defaultFallbackValues, isDirty, reset]);

  useEffect(() => {
    const section0 = configDefaults?.sections?.[0];
    if (!section0 || section0.type !== 'Introduction') return;
    setPrimaryColor(section0.primaryColor);
  }, [configDefaults]);

  useRouterWillLeave(isDirty || isUploading, {
    message: isUploading
      ? 'An upload is currently in progress. Leaving the page will cancel the upload.'
      : 'You have unsaved changes. Are you sure you want to leave without saving?',
  });

  useEffect(() => {
    props.onDirtyChange?.(isDirty || isUploading);
  }, [isDirty, isUploading, props.onDirtyChange]);

  // Track "Landing Page Edited" when user enters the builder
  const lpType = isOfferMode ? 'offer' : 'otp';
  useEffect(() => {
    trackEvent({ type: 'Landing Page Edited', payload: { type: lpType } });
  }, [lpType]);

  const sectionMethods = useSections(formMethods);

  const introSectionPrimary = watch('sections.0.primaryColor');
  useEffect(
    function broadcastPrimaryColorFromIntro() {
      setPrimaryColor(introSectionPrimary);

      const sections = watch('sections');
      const updatedSections = sections.map((s) => {
        return {
          ...s,
          bgColor: colors[s.colorIndex],
        };
      });
      setValue('sections', updatedSections);
    },
    [introSectionPrimary, setValue, watch, colors],
  );

  const reportBackendSchemaIssues = (issues: ZodIssue[]) => {
    issues.forEach((issue) => {
      if (!issue.path || issue.path.length < 3) return;
      if (issue.path[0] !== 'sections') return;
      const sectionIndex = issue.path[1];
      if (typeof sectionIndex !== 'number') return;
      const fieldPath = issue.path.slice(2).join('.');
      if (!fieldPath) return;

      setError(`sections.${sectionIndex}.${fieldPath}` as any, {
        type: 'backend',
        message: issue.message,
      });
    });
  };

  // Define all mutations
  const [addPurchasePageMutation] = useAddPurchasePageConfigMutation({
    refetchQueries: [
      {
        query: PurchasePageConfigDocument,
        variables: { programId },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [updatePurchasePageMutation] = useUpdatePurchasePageConfigMutation({
    refetchQueries: [
      {
        query: PurchasePageConfigDocument,
        variables: { programId },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [addOfferLandingPageMutation] = useAddOfferLandingPageConfigMutation({
    refetchQueries: [
      {
        query: OfferLandingPageConfigDocument,
        variables: {
          influencerId,
          parentId: Number(props.params.offerId),
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [updateOfferLandingPageMutation] =
    useUpdateOfferLandingPageConfigMutation({
      refetchQueries: [
        {
          query: OfferLandingPageConfigDocument,
          variables: {
            influencerId,
            parentId: Number(props.params.offerId),
          },
        },
      ],
      awaitRefetchQueries: true,
    });

  const onSave = handleSubmit(
    (values) => {
      const { sections } = state;

      const resFE = OTPLandingPageSchema.safeParse({ sections });

      if (!resFE.success) {
        afterSaveRedirectRef.current = null;
        toast.error('invalid values schema1 - please contact support');
        return;
      }

      const parsedSections = resFE.data.sections as unknown as ISection[];
      const cleanedSections = cleanupSectionsType(parsedSections as any);
      const normalizedSections = ensureIntroductionDefaults(
        cleanedSections,
        primaryColor || '#000000',
      );
      const extendedSections = extendSectionsType(normalizedSections);
      const config = {
        // `workoutsPreviewEnabled` is a UI-only flag — never send it to the API.
        sections: stripOfferUiFields(normalizedSections),
      };

      // Only validate with PurchasePageConfigSchemaV1 for OTP mode
      // Offer mode uses different schema that includes OfferDetails
      if (!props.isOfferMode) {
        clearErrors();
        const resBE = PurchasePageConfigSchemaV1.safeParse(config);
        if (resBE.success === false) {
          afterSaveRedirectRef.current = null;
          reportBackendSchemaIssues(resBE.error.issues);
          return;
        }
      }

      setIsSaving(true);

      // Choose mutation based on mode
      let mutation;
      let data: any;
      const backendConfigId = props.backendConfig?.id;
      const hasExistingConfig =
        backendConfigId !== null && backendConfigId !== undefined;

      if (props.isOfferMode) {
        // Offer mode: use universal mutation
        const baseData = {
          influencerId,
          parentId: Number(props.params.offerId),
          type: 'OFFER' as const,
          config,
        };

        // Use props.backendConfig.id directly instead of local state
        if (hasExistingConfig) {
          // Update existing config
          data = { ...baseData, id: backendConfigId };
          mutation = updateOfferLandingPageMutation;
        } else {
          // Create new config
          data = baseData;
          mutation = addOfferLandingPageMutation;
        }
      } else {
        // OTP mode: use props.backendConfig.id directly
        data = { programId, config };
        if (hasExistingConfig) data.id = backendConfigId;

        mutation = hasExistingConfig
          ? updatePurchasePageMutation
          : addPurchasePageMutation;
      }

      mutation({ variables: { data } })
        .then(() => {
          toast.success('Page saved successfully!');
          setPersistedConfig((prev) => ({
            ...(prev ?? {}),
            sections: extendedSections,
          }));
          formMethods.reset({
            ...values,
            sections: extendedSections,
          });
          trackEvent({ type: 'Landing Page Saved', payload: { type: lpType } });

          // Redirect after a successful save (once the form is clean).
          if (afterSaveRedirectRef.current) {
            setPendingRedirectUrl(afterSaveRedirectRef.current);
            afterSaveRedirectRef.current = null;
          }
        })
        .catch((e) => {
          afterSaveRedirectRef.current = null;
          toast.error(`Error during save: ${String(e)}`, {
            duration: 10000,
          });
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    () => {
      afterSaveRedirectRef.current = null;
      toast.error('Please fix highlighted errors before saving.');
    },
  );

  const handleWorkoutRedirect = (
    workoutId: number,
    programId?: number | null,
  ) => {
    const url = PATHS.WORKOUT_EDIT({ workoutId, influencerId, programId });
    if (isDirty) {
      setConfirmRedirectUrl(url);
    } else {
      navigate(url);
    }
  };

  const handleDiscardAndRedirect = () => {
    const url = confirmRedirectUrl;
    setConfirmRedirectUrl(null);
    if (!url) return;
    // Reset to the last saved config so the form is clean before leaving.
    reset(configDefaults ?? defaultFallbackValues);
    setPendingRedirectUrl(url);
  };

  const handleSaveAndRedirect = () => {
    const url = confirmRedirectUrl;
    setConfirmRedirectUrl(null);
    if (!url) return;
    afterSaveRedirectRef.current = url;
    onSave();
  };

  const addSection =
    (index: number) => (sectionType: ISection['type'] | string) => {
      const sections = watch('sections');
      const newSections = [...sections];
      trackEvent({ type: 'Section Added', payload: { type: lpType } });

      // Always use defaultOTPPageConfig to get section templates
      // Even in offer mode, we need the full list of section types available
      const defaultConfig = defaultOTPPageConfig(program, {
        primaryColor,
        colors,
        landingPageConfig,
      });

      const sectionByType = keyBy(defaultConfig.sections, 'type');

      let newSection = sectionByType[sectionType];

      // The OfferDetails template is not part of defaultOTPPageConfig; it only
      // exists in the offer page config. Source it from there when adding it.
      if (!newSection && (sectionType as string) === 'OfferDetails') {
        const offerConfig = loadOfferPageConfig(
          program,
          null,
          landingPageConfig,
          { primaryColor, colors },
          offerData,
          { title: offerTitle },
        );
        newSection = keyBy(offerConfig.sections, 'type')[sectionType];
      }

      if (!newSection) {
        console.error('Section type not found in defaultConfig:', sectionType);
        return;
      }

      if ((sectionType as string) === 'OfferDetails') {
        const typedSection = newSection as unknown as OfferDetailsSectionSchema;
        const headline =
          offerTitle && offerTitle.trim().length > 0
            ? offerTitle
            : typedSection.headlineText;

        newSection = {
          ...typedSection,
          headlineText: headline,
          programIds: offerData
            ? offerData.programIds.options.map((opt) => opt.value)
            : typedSection.programIds,
          extrasIds: offerData
            ? offerData.extrasIds.options.map((opt) => opt.value)
            : typedSection.extrasIds,
          communityFeatures: offerData
            ? offerData.communityFeatures.options.map((opt) => opt.value)
            : typedSection.communityFeatures,
        } as OfferDetailsSectionSchema;
      }

      newSections.splice(index + 1, 0, newSection as ISection);

      setValue('sections', newSections, { shouldDirty: true });

      const newSectionIndex = index + 1;
      setCollapsed(collapseOther(newSectionIndex));
    };

  const handleSectionChange = (index) => (delta: Partial<SectionSchema>) => {
    const section = watch(`sections.${index}`);
    const s = { ...section, ...delta } as ISection;
    setValue(`sections.${index}`, s, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const registerInSection = (index) => (path) =>
    formMethods.register(`sections.${index}.${path}` as any);

  const [preview, setPreview] = useState<PreviewType>(
    props.isOfferMode ? 'desktop-offer' : 'desktop-otp',
  );

  const previewModal = useModal(PreviewModal);

  const state = watch();
  const getErrorIndexes = () => {
    if (!formState.errors.sections) return [];
    return formState.errors.sections.reduce(
      (acc, v, i) => (v && Object.keys(v).length > 0 ? [...acc, i] : acc),
      [] as number[],
    );
  };
  const isErrorSection = (sectionIndex: number) =>
    getErrorIndexes().includes(sectionIndex);

  const ensureErrorSectionsAreOpen = (collapsed: boolean[]) => {
    const errorIndexes = getErrorIndexes();
    if (errorIndexes.length === 0) return null;
    return collapsed.map((v, i) => (errorIndexes.includes(i) ? false : v));
  };

  const collapseOther = (index: number) =>
    state.sections.map((_, i) => i !== index);

  const [collapsed, _setCollapsed] = useState(collapseOther(0));

  const setCollapsed = (nextState: boolean[]) =>
    _setCollapsed(ensureErrorSectionsAreOpen(nextState) || nextState);

  useEffect(() => {
    const nextCollapsed = ensureErrorSectionsAreOpen(collapsed);
    if (nextCollapsed) _setCollapsed(nextCollapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.errors.sections]);

  const showPreview = (type: PreviewType) => {
    setPreview(type);
    previewModal.open();
    trackEvent({ type: 'Preview', payload: { type: lpType } });
  };

  const handleLandingPageOpen = () => {
    if (!program) return;
    window.open(
      toProgramLandingPageUrl({
        id: program.id,
        name: program.name,
        influencer: {
          customURL: program.influencer.customURL,
        },
      }),
      '_blank',
    );
  };

  const handleOfferLandingPageOpen = () => {
    if (!offerLandingPageUrl) return;
    window.open(offerLandingPageUrl, '_blank');
  };

  const disableLPLink = program
    ? !program.publishedAt && !props.isCreatorLive
    : true;
  const showLPLink =
    props.backendConfig?.id !== null &&
    props.backendConfig?.id !== undefined &&
    program &&
    !program.archivedAt;
  const showOfferLandingLink =
    props.isOfferMode && Boolean(offerLandingPageUrl) && isOfferPublished;

  const header = (
    <Row
      gap="xl"
      className={sprinkles({
        marginBottom: 'xl',
        justifyContent: 'space-between',
        alignItems: 'center',
      })}
    >
      <Col>
        <Typography as="div" variant="body" size="large">
          {isOffers35Enabled ? (
            <>
              Customize this automated page to suit your needs. A strong landing
              page is vital for success on Playbook and enhances your visibility
              on search engines like Google. Create an engaging page to attract
              buyers.{' '}
              <a
                href={HELP_LINKS.otpOffersMigration}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline' }}
              >
                Discover the importance of an Offer Landing page.
              </a>
            </>
          ) : (
            'Your landing page is crucial for success on Playbook and can boost visibility on search engines. Create a compelling page to attract buyers.'
          )}
        </Typography>
      </Col>
      <Row
        gap="xl"
        className={sprinkles({
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <Col
          onClick={() =>
            showPreview(props.isOfferMode ? 'desktop-offer' : 'desktop-otp')
          }
          className={sprinkles({ px: 'm', justifyContent: 'center' })}
        >
          <Tooltip title="Show Desktop version preview" variant="dark">
            <FontAwesomeIcon
              icon={faDesktop}
              style={{ width: '23px', height: '20px' }}
            />
          </Tooltip>
        </Col>
        <Col
          onClick={() =>
            showPreview(props.isOfferMode ? 'mobile-offer' : 'mobile-otp')
          }
          className={sprinkles({ px: 'm', justifyContent: 'center' })}
        >
          <Tooltip title="Show Mobile version preview" variant="dark">
            <FontAwesomeIcon
              style={{ width: '15px', height: '20px' }}
              icon={faMobile}
            />
          </Tooltip>
        </Col>
        {showLPLink && (
          <NewButton
            onClick={handleLandingPageOpen}
            variant="text"
            leftIcon={faExternalLink}
            disabled={disableLPLink || props.readonly}
          >
            Open
          </NewButton>
        )}
        {showOfferLandingLink && (
          <NewButton
            onClick={handleOfferLandingPageOpen}
            variant="text"
            leftIcon={faExternalLink}
            disabled={props.readonly}
          >
            Open
          </NewButton>
        )}
        <NewButton
          onClick={onSave}
          variant="filled"
          loading={isSaving}
          disabled={props.readonly || hasConfigError}
        >
          Save
        </NewButton>
      </Row>
    </Row>
  );
  const previewURL =
    import.meta.env.VITE_LANDING_PAGE_URL +
    (props.isOfferMode
      ? `/offers/preview/${props.params.offerId}`
      : `/otp/preview/${programId}`);
  return (
    <CreatorContext.Provider value={props.program?.influencer || null}>
      <ProgramContext.Provider value={props.program || null}>
        <FormProvider {...formMethods}>
          <div
            className="landing-page-builder text-gray-1"
            style={{ width: '100%' }}
          >
            <div className="text-red-500">{props.error?.message}</div>
            {header}
            <div className="mb-6">
              <Errors data={toErrors(state.sections, formState.errors)} />
            </div>
            {hasConfigError && (
              <div className="mb-6">
                <Errors
                  data={{
                    title: 'Configuration Error',
                    messages: [
                      'Failed to load landing page config. Please make sure the creator profile is complete (avatar, bio, etc.)',
                    ],
                  }}
                />
              </div>
            )}

            {!props.error ? (
              <>
                <div className={props.embedded ? '' : 'p-2 md:p-10'}>
                  {state.sections.map((section, index) => {
                    // ProgramStats shows a single program's stats, which is
                    // meaningless when the offer bundles more than one program.
                    // Don't render it in the editor at all in that case.
                    const programCount =
                      offerData?.programIds.options.length ?? 0;
                    if (
                      (section as { type?: string }).type === 'ProgramStats' &&
                      programCount > 1
                    ) {
                      return null;
                    }
                    const sectionUpdate = handleSectionChange(index);
                    const register = registerInSection(index);
                    const color = colors[(section.colorIndex as number) ?? 2];
                    const canMoveUp = index > 1;
                    const canMoveDown =
                      index > 0 && index < state.sections.length - 1;
                    const folded = collapsed[index];

                    return (
                      <SectionContainer
                        key={index}
                        onAddSection={addSection(index)}
                        onDeleteSection={
                          index > 0
                            ? () => {
                                sectionMethods.deleteSection(index)();
                                trackEvent({
                                  type: 'Section removed',
                                  payload: { type: lpType },
                                });
                              }
                            : undefined
                        }
                        onDuplicateSection={sectionMethods.duplicateSection(
                          index,
                        )}
                        title={toSectionTitle(section)}
                        enabled={section.enabled}
                        onChangeEnabled={(enabled) => {
                          sectionUpdate(onEnabledChange(section, enabled));
                        }}
                        colors={colors}
                        color={color}
                        onColorIndexChange={(colorIndex) => {
                          sectionUpdate({
                            colorIndex,
                            bgColor: colors[colorIndex],
                          });
                        }}
                        alwaysEnabled={index === 0}
                        onMove={sectionMethods.handleSectionMove(index)}
                        canMoveUp={canMoveUp}
                        canMoveDown={canMoveDown}
                        folded={folded}
                        setFolded={(v) => {
                          setCollapsed(collapseOther(v ? -1 : index));
                        }}
                        hasError={isErrorSection(index)}
                        id={`sections.${index}`}
                        hasOfferId={!!offerData}
                        builderType={
                          offerData ? 'offer-landing-page' : 'otp-landing-page'
                        }
                      >
                        <SectionRenderer
                          value={section as SectionSchema}
                          onChange={sectionUpdate}
                          influencerId={influencerId}
                          register={register}
                          index={index}
                          program={program}
                          primaryColor={primaryColor}
                          onUpload={universalUpload}
                          offerData={offerData}
                          onWorkoutClick={handleWorkoutRedirect}
                        />
                      </SectionContainer>
                    );
                  })}
                </div>
                <previewModal.Popup
                  data={{
                    ...state,
                    sections: cleanupSectionsType(state.sections),
                  }}
                  previewUrl={previewURL}
                  previewType={preview}
                />
                <UnsavedChangesRedirectModal
                  isOpen={confirmRedirectUrl !== null}
                  onClose={() => setConfirmRedirectUrl(null)}
                  onDiscard={handleDiscardAndRedirect}
                  onSave={handleSaveAndRedirect}
                />
              </>
            ) : null}
          </div>
        </FormProvider>
      </ProgramContext.Provider>
    </CreatorContext.Provider>
  );
}
